import React, { useState, useEffect, useRef } from 'react';
import { SECTIONS, getTier, PRIMARY_STORAGE_URL, sanitizePayload } from './constants';
import { Respondent, Responses, ScanResult, SectionScore } from './types';
import Landing from './components/Landing';
import Questionnaire from './components/Questionnaire';
import Results from './components/Results';

type FlowStep = 'landing' | 'scan' | 'results';

const App: React.FC = () => {
  const [step, setStep] = useState<FlowStep>('landing');
  const [respondent, setRespondent] = useState<Respondent | null>(null);
  const [responses, setResponses] = useState<Responses>({ A: [], B: [], C: [], D: [], E: [] });
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [isSuccess, setIsSuccess] = useState(false);
  const appRef = useRef<HTMLDivElement>(null);
  const lastHeight = useRef<number>(0);

  // Helper to trigger parent scroll
  const triggerScroll = () => {
    window.parent.postMessage({ type: 'scrollToTop' }, '*');
    window.scrollTo({ top: 0, behavior: 'smooth' });
  };

  // Scroll to top on every major step change
  useEffect(() => {
    triggerScroll();
  }, [step]);

  // iFrame Resizer logic - Optimized for WordPress integration
  useEffect(() => {
    const reportHeight = () => {
      if (appRef.current) {
        const height = appRef.current.scrollHeight;
        if (Math.abs(height - lastHeight.current) > 10) {
          lastHeight.current = height;
          window.parent.postMessage({ 
            type: 'setHeight', 
            height: height + 30 
          }, '*');
        }
      }
    };

    reportHeight();
    const observer = new ResizeObserver(() => {
      window.requestAnimationFrame(reportHeight);
    });
    
    if (appRef.current) observer.observe(appRef.current);
    window.addEventListener('resize', reportHeight);
    
    return () => {
      observer.disconnect();
      window.removeEventListener('resize', reportHeight);
    };
  }, [step]);

  const handleStartScan = () => {
    setStep('scan');
  };

  const handleScanComplete = (finalResponses: Responses) => {
    setResponses(finalResponses);
    setStep('results');
  };

  const calculateResults = (finalRespondent: Respondent | null): ScanResult | null => {
    if (!finalRespondent) return null;
    const sectionScores: SectionScore = {};
    let totalScore = 0;
    Object.entries(responses).forEach(([key, valuesArray]) => {
      const values = valuesArray as number[];
      const sum = values.reduce((a, b) => a + b, 0);
      sectionScores[key] = values.length > 0 ? Number((sum / values.length).toFixed(1)) : 0;
      totalScore += sum;
    });
    const urlParams = new URLSearchParams(window.location.search);
    return {
      respondent: finalRespondent,
      responses,
      sectionScores,
      totalScore,
      tier: getTier(totalScore),
      timestamp: new Date().toISOString(),
      utm: {
        source: urlParams.get('utm_source') || "",
        medium: urlParams.get('utm_medium') || "",
        campaign: urlParams.get('utm_campaign') || ""
      }
    };
  };

  const handleSubmit = async (finalRespondent: Respondent) => {
    setRespondent(finalRespondent);
    const results = calculateResults(finalRespondent);
    if (!results) return;
    setIsSubmitting(true);
    try {
      const payload = sanitizePayload({
        ...results,
        sendResultsEmail: false, 
        meta: { ts: new Date().toISOString(), source: "Lead Capture" }
      });
      await fetch(PRIMARY_STORAGE_URL, {
        method: 'POST',
        headers: { 'Content-Type': 'application/json' },
        body: JSON.stringify(payload)
      });
      setIsSuccess(true);
      setTimeout(() => setIsSuccess(false), 5000);
    } catch (err) {
      console.warn('Storage error:', err);
    } finally {
      setIsSubmitting(false);
    }
  };

  return (
    <div ref={appRef} className="flex flex-col items-center justify-start p-4 py-4 md:py-10 bg-transparent overflow-hidden">
      <div className="w-full max-w-2xl glass-panel rounded-[2.5rem] overflow-hidden relative shadow-2xl">
        <div className="absolute top-0 left-0 w-full h-1 bg-gradient-to-r from-transparent via-primary to-transparent opacity-40"></div>
        
        {step === 'landing' && <Landing onStart={handleStartScan} />}
        {step === 'scan' && (
          <Questionnaire 
            onComplete={handleScanComplete} 
            onBack={() => setStep('landing')}
          />
        )}
        {step === 'results' && (
          <Results 
            responses={responses}
            onReset={() => {
              setStep('landing');
              setRespondent(null);
              setResponses({ A: [], B: [], C: [], D: [], E: [] });
            }}
            onSubmit={handleSubmit}
            isSubmitting={isSubmitting}
            isSuccess={isSuccess}
            calculateResults={calculateResults}
          />
        )}
      </div>

      <footer className="mt-10 mb-6 text-center text-[10px] text-gray-600 font-bold tracking-widest uppercase">
        <div className="flex items-center justify-center gap-6 mb-3 opacity-60">
          <span>SECURED AUDIT</span>
          <div className="w-1 h-1 rounded-full bg-primary"></div>
          <span>CONFIDENTIAL STRATEGY</span>
        </div>
        &copy; {new Date().getFullYear()} DDG EUROPE.
      </footer>
    </div>
  );
};

export default App;